import { Button } from '@sorrem-client/ui/components';
import { useAppNavigate } from '@sorrem-client/ui/hooks';
import type { PartyIssue } from '../party-realtime-store';
import styles from './GatheringPartyStatus.module.scss';

interface GatheringPartyStatusProps {
    readonly isConnected: boolean;
    /** Whether the party state ever arrived, telling "still connecting" from "connection lost after connecting". */
    readonly hasEverConnected: boolean;
    readonly hasCrucible: boolean;
    readonly sceneError: string | null;
    /** Why the server just refused to start the run (#214), or null. */
    readonly startNotice: string | null;
    readonly partyIssue: PartyIssue | null;
    /** The run started but its host never said where to connect: the page stops waiting and says so. */
    readonly isStartStalled: boolean;
    /** A direct-entry run asks again with Try again; a party run retries by readying up instead. */
    readonly onRetryDirectEntry: (() => void) | null;
}

/** The party connection's status and issues: over the gathering, or under the loading bar. */
export function GatheringPartyStatus({
    isConnected,
    hasEverConnected,
    hasCrucible,
    sceneError,
    startNotice,
    partyIssue,
    isStartStalled,
    onRetryDirectEntry,
}: GatheringPartyStatusProps): JSX.Element {
    const navigate = useAppNavigate();
    const isFatal = partyIssue !== null && partyIssue.kind !== 'notice';
    return (
        <div className={styles.partyStatus}>
            {!isConnected && !hasEverConnected && !isFatal && (
                <p className={styles.status}>Connecting to party state...</p>
            )}
            {!isConnected && hasEverConnected && !isFatal && (
                <p className={styles.status}>Connection to the party was lost. Retrying...</p>
            )}
            {isConnected && !hasCrucible && <p className={styles.status}>Waiting for party details...</p>}
            {sceneError !== null && <p className={styles.error}>{sceneError}</p>}
            {startNotice !== null && !isFatal && (
                <div className={styles.errorBlock} role="status">
                    <p className={styles.status}>{startNotice}</p>
                    {onRetryDirectEntry !== null && (
                        <Button variant="secondary" onClick={onRetryDirectEntry}>
                            Try again
                        </Button>
                    )}
                </div>
            )}
            {partyIssue?.kind === 'notice' && (
                <p className={styles.status} role="status">{partyIssue.message}</p>
            )}
            {isStartStalled && !isFatal && (
                <div className={styles.errorBlock} role="status">
                    <p className={styles.status}>The Crucible is taking longer than it should to open.</p>
                    <Button variant="secondary" onClick={() => navigate('/character/crucibles')}>
                        Return to character
                    </Button>
                </div>
            )}

            {isFatal && (
                <div className={styles.errorBlock}>
                    <p className={styles.error}>{partyIssue.message}</p>
                    {partyIssue.kind === 'session-expired'
                        ? (
                            <Button variant="secondary" onClick={() => navigate('/login')}>
                                Log in again
                            </Button>
                        )
                        : (
                            <Button variant="secondary" onClick={() => navigate('/character/crucibles')}>
                                Return to character
                            </Button>
                        )}
                </div>
            )}
        </div>
    );
}
